import { ValuesType } from 'utility-types'
import { z } from 'zod'

import { Ability } from 'common/core/constants/Ability'
import { AbilityState } from 'common/core/constants/AbilityState'

import { LimitBasedAbilities } from './constants'

const CommonAbilitySchema = z.object({
  name: z.string(),
  state: z.enum(AbilityState),
})

const LimitBasedAbilitySchema = CommonAbilitySchema.extend({
  used: z.number().nonnegative(),
  limit: z.number().nonnegative(),
})

export const LegacyAbilitySchema = z.enum(Ability)

export const LegacyAbilitiesSchema = z.array(LegacyAbilitySchema)

export const AbilitiesMapSchema = z.object({
  [Ability.AI_ALWAYS_ON]: CommonAbilitySchema.extend({
    name: z.literal(Ability.AI_ALWAYS_ON),
  }),
  [Ability.AI_KEYWORD]: CommonAbilitySchema.extend({
    name: z.literal(Ability.AI_KEYWORD),
  }),
  [Ability.AI_AGENT]: CommonAbilitySchema.extend({
    name: z.literal(Ability.AI_AGENT),
  }),
  [Ability.AGENCY_ANALYTICS]: CommonAbilitySchema.extend({
    name: z.literal(Ability.AGENCY_ANALYTICS),
  }),
  [Ability.AUTO_ASSIGN]: CommonAbilitySchema.extend({
    name: z.literal(Ability.AUTO_ASSIGN),
  }),
  [Ability.COMMON_INTEGRATIONS]: CommonAbilitySchema.extend({
    name: z.literal(Ability.COMMON_INTEGRATIONS),
  }),
  [Ability.BROADCASTING]: CommonAbilitySchema.extend({
    name: z.literal(Ability.BROADCASTING),
  }),
  [Ability.EMAIL_CHANNEL]: CommonAbilitySchema.extend({
    name: z.literal(Ability.EMAIL_CHANNEL),
  }),
  [Ability.PAID_AUTOMATIONS]: CommonAbilitySchema.extend({
    name: z.literal(Ability.PAID_AUTOMATIONS),
  }),
  [Ability.LIVECHAT]: CommonAbilitySchema.extend({
    name: z.literal(Ability.LIVECHAT),
  }),
  [Ability.SMS_CHANNEL]: CommonAbilitySchema.extend({
    name: z.literal(Ability.SMS_CHANNEL),
  }),
  [Ability.WA_CHANNEL]: CommonAbilitySchema.extend({
    name: z.literal(Ability.WA_CHANNEL),
  }),
  [Ability.SPREADSHEET_INTEGRATION]: CommonAbilitySchema.extend({
    name: z.literal(Ability.SPREADSHEET_INTEGRATION),
  }),
  [Ability.IMPORT]: CommonAbilitySchema.extend({
    name: z.literal(Ability.IMPORT),
  }),

  [Ability.FREE_MC_BRANDING]: CommonAbilitySchema.extend({
    name: z.literal(Ability.FREE_MC_BRANDING),
  }),

  [Ability.SMART_SEGMENTS]: CommonAbilitySchema.extend({
    name: z.literal(Ability.SMART_SEGMENTS),
  }),

  [Ability.MESSAGE_TAGS]: CommonAbilitySchema.extend({
    name: z.literal(Ability.MESSAGE_TAGS),
  }),

  // Limit based
  //
  [Ability.USERS_TOTAL]: LimitBasedAbilitySchema.extend({
    name: z.literal(Ability.USERS_TOTAL),
  }),
  [Ability.LIVECHAT_SEATS]: LimitBasedAbilitySchema.extend({
    name: z.literal(Ability.LIVECHAT_SEATS),
  }),
  [Ability.VIEWERS]: LimitBasedAbilitySchema.extend({
    name: z.literal(Ability.VIEWERS),
  }),
  [Ability.CHANNELS_COUNT]: LimitBasedAbilitySchema.extend({
    name: z.literal(Ability.CHANNELS_COUNT),
  }),
})

export const AbilitiesUnsafeMapSchema = z.record(
  z.string(),
  z.object({
    name: z.string().optional(),
    state: z.enum(AbilityState),
  }),
)

export const AbilitiesDataSchema = z.union([LegacyAbilitiesSchema, AbilitiesMapSchema])

export type AbilitiesData = z.infer<typeof AbilitiesDataSchema>

export type AbilitiesMap = z.infer<typeof AbilitiesMapSchema>

export type GetAbilityDescriptor<AbilityName extends Ability> = AbilitiesMap[AbilityName]

export type AbilityDescriptor = ValuesType<AbilitiesMap>

export type LimitBasedAbilityNames = typeof LimitBasedAbilities[number]

export type CommonAbilityState = AbilityState.BLOCKED | AbilityState.LIMITED | AbilityState.ENABLED

export interface AbilityLimits {
  used: number
  limit: number
}

type WithoutLimitedAbilities<T> = T extends LimitBasedAbilityNames ? never : T

export type CommonAbilityNames = WithoutLimitedAbilities<Ability>
